import pLimit from "p-limit";
import { API, graphqlOperation } from "aws-amplify";

import { listCarriersForDbaBackfill } from "../../graphql/carrier/carrier-queries";
import { setCarrierDbaName } from "../../graphql/carrier/carrier-mutations";
import { fetchAllPages } from "../../utils/appUtils";
import { lookupCarrierIdentifier } from "../../utils/carrierLookup";

/**
 * Fill Carrier.dbaName from FMCSA's register, for the entries written before
 * the field existed.
 *
 * The register is the only source there is: a yard types the legal name off the
 * paperwork, and the trading name — the one painted on the truck door, and the
 * one a driver types at the kiosk — is what FMCSA holds against the USDOT
 * number. So every entry carrying a USDOT number and no trading name is looked
 * up, and the register's dbaName is written when it says something the business
 * name does not already.
 *
 * USDOT only. A docket number (MC/MX) can be held by more than one carrier and
 * the lookup refuses to pick one, so there is nothing to write from it.
 *
 * Never overwrites. An entry with a dbaName already — typed by the yard, or
 * written by an earlier run — is skipped, so a manual correction survives the
 * button being pressed again. Safe to run twice for the same reason.
 *
 * Admin only: a yard's session reads its own carrier rows and no others, so run
 * as anyone else this would report the book as already done.
 */

const PAGE = 500;
// A backfill that walks a short list has skipped carriers and no way to know
// it, so fetchAllPages' runaway guard is set high enough to hold every carrier
// in the book and made fatal rather than warned: a truncated read here would
// report the book as done.
const MAX_PAGES = 200;

// Three at a time. The webKey behind /carrierLookup/verify is rate-limited per
// account, and the whole book platform-wide is a few hundred carriers at most —
// a minute of sequential-ish lookups, not something worth going wide on.
const LOOKUP_CONCURRENCY = 3;

const squash = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

/** The USDOT number an entry carries, or null. */
export const usdotOf = (carrier) => {
  const hit = (carrier?.identifiers || []).find(
    (identifier) =>
      identifier &&
      String(identifier.scheme || "").toUpperCase() === "USDOT" &&
      String(identifier.value || "")
        .replace(/\D/g, "")
        .replace(/^0+/, ""),
  );
  return hit ? hit.value : null;
};

/** Whether an entry is worth asking the register about. */
export const needsDbaBackfill = (carrier) =>
  Boolean(carrier?.id) &&
  !String(carrier.dbaName || "").trim() &&
  usdotOf(carrier) !== null;

/**
 * What one lookup answer means for one entry.
 *
 * @returns {{write: string}|{skip: string}} the dbaName to write, or why not:
 *   "unavailable" (register down or key misconfigured), "not-found" (no carrier
 *   holds the number), "no-dba" (the register lists no trading name), or
 *   "same-as-name" (the trading name is the business name already, so writing
 *   it would only put the same words on the kiosk row twice).
 */
export const planDba = (carrier, lookup) => {
  if (!lookup || lookup.unavailable) return { skip: "unavailable" };
  if (!lookup.found || lookup.ambiguous || !lookup.carrier)
    return { skip: "not-found" };
  const dbaName = String(lookup.carrier.dbaName || "").trim();
  if (!dbaName) return { skip: "no-dba" };
  if (squash(dbaName) === squash(carrier.businessName))
    return { skip: "same-as-name" };
  return { write: dbaName };
};

/**
 * @returns {Promise<{scanned:number, candidates:number, filled:number,
 *   unavailable:number, notFound:number, noDba:number, sameAsName:number,
 *   failed:number}>}
 */
export const backfillCarrierDba = async () => {
  const counts = {
    scanned: 0,
    candidates: 0,
    filled: 0,
    unavailable: 0,
    notFound: 0,
    noDba: 0,
    sameAsName: 0,
    failed: 0,
  };
  const SKIP_COUNTER = {
    unavailable: "unavailable",
    "not-found": "notFound",
    "no-dba": "noDba",
    "same-as-name": "sameAsName",
  };

  const carriers = await fetchAllPages({
    query: listCarriersForDbaBackfill,
    responseKey: "listCarriers",
    pageLimit: PAGE,
    maxPages: MAX_PAGES,
    label: "adminCarrierDbaBackfill",
    throwOnTruncation: true,
  });
  counts.scanned = carriers.length;
  const candidates = carriers.filter(needsDbaBackfill);
  counts.candidates = candidates.length;

  const limit = pLimit(LOOKUP_CONCURRENCY);
  await Promise.all(
    candidates.map((carrier) =>
      limit(async () => {
        try {
          // lookupCarrierIdentifier caches per number, so two yards' entries
          // for the same carrier cost the register one call.
          const lookup = await lookupCarrierIdentifier({
            scheme: "USDOT",
            value: usdotOf(carrier),
          });
          const plan = planDba(carrier, lookup);
          if (plan.skip) {
            counts[SKIP_COUNTER[plan.skip]] += 1;
            return;
          }
          await API.graphql(
            graphqlOperation(setCarrierDbaName, {
              input: { id: carrier.id, dbaName: plan.write },
            }),
          );
          counts.filled += 1;
        } catch (err) {
          counts.failed += 1;
          console.warn(
            `backfillCarrierDba: ${carrier.id}`,
            err?.message || err,
          );
        }
      }),
    ),
  );

  console.log("Carrier dbaName backfill:", counts);
  return counts;
};
